import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, BACKEND_URL } from "@/lib/api";
import { Plus, X, FileStack, MoreVertical, Copy, Archive, Wand2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";

const categories = ["Safe Work Instruction","Standard Operating Procedure","Maintenance Procedure","Inspection Form","Policy","Technical Report","Checklist","Risk Assessment","Custom"];

export default function TemplatesPage() {
  const { activeWorkspace } = useAuth();
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/workspaces/${activeWorkspace.id}/templates`);
    setItems(data || []);
  };
  useEffect(() => { if (activeWorkspace) load(); /* eslint-disable-next-line */ }, [activeWorkspace]);

  const duplicate = async (id) => {
    setMenuOpen(null);
    try { await api.post(`/workspaces/${activeWorkspace.id}/templates/${id}/duplicate`); toast.success("Duplicated"); load(); }
    catch { toast.error("Could not duplicate"); }
  };
  const archive = async (id) => {
    setMenuOpen(null);
    if (!window.confirm("Archive this template? It won't appear in new-document flows.")) return;
    try { await api.delete(`/workspaces/${activeWorkspace.id}/templates/${id}`); toast.success("Archived"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not archive"); }
  };

  return (
    <div className="max-w-6xl mx-auto p-8" data-testid="templates-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Templates</h1>
          <p className="text-sm text-slate-400">Company document structures. Click any template to customise sections, upload a logo, or reshape it from an existing Word file.</p>
        </div>
        <button onClick={()=>setShowNew(true)} className="fx-btn-primary text-white text-sm font-medium px-4 py-2 rounded-lg inline-flex items-center gap-2" data-testid="add-template"><Plus size={14}/> Add template</button>
      </div>
      {items.length === 0 ? (
        <div className="fx-panel p-10 text-center">
          <FileStack size={28} className="mx-auto text-cyan-300"/>
          <div className="mt-3 text-slate-300 font-medium">No templates yet.</div>
          <div className="text-sm text-slate-500 mt-1">Your templates teach Fexidoc how your organisation structures documents.</div>
          <button onClick={()=>setShowNew(true)} className="mt-4 fx-btn-primary text-white text-sm font-medium px-4 py-2 rounded-lg">Add your first template</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 min-w-0">
          {items.map((t) => (
            <div key={t.id} className="fx-panel p-5 fx-hover-panel relative min-w-0 overflow-hidden" data-testid={`template-card-${t.id}`}>
              <Link to={`/app/templates/${t.id}`} className="absolute inset-0 rounded-lg" aria-label={t.name}/>
              <div className="flex items-start justify-between gap-2 relative">
                <div className="flex items-center gap-3 min-w-0">
                  <TemplateLogo template={t}/>
                  <div className="min-w-0">
                    <div className="mono text-[10px] uppercase tracking-widest text-slate-500">{t.category}</div>
                    <div className="font-display text-base font-semibold text-slate-100 truncate">{t.name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 relative z-10">
                  <StatusBadge status={t.status}/>
                  <button onClick={(e)=>{e.preventDefault(); e.stopPropagation(); setMenuOpen(menuOpen===t.id ? null : t.id);}} className="text-slate-500 hover:text-white p-1" data-testid={`template-menu-${t.id}`}><MoreVertical size={14}/></button>
                  {menuOpen === t.id && (
                    <div className="absolute right-0 top-full mt-1 fx-panel-2 rounded-lg shadow-xl w-40 z-20" onClick={(e)=>e.stopPropagation()}>
                      <button onClick={(e)=>{e.preventDefault(); duplicate(t.id);}} className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-white/5 inline-flex items-center gap-2" data-testid={`duplicate-${t.id}`}><Copy size={12}/>Duplicate</button>
                      <button onClick={(e)=>{e.preventDefault(); archive(t.id);}} className="w-full text-left px-3 py-2 text-xs text-red-300 hover:bg-white/5 inline-flex items-center gap-2" data-testid={`archive-${t.id}`}><Archive size={12}/>Archive</button>
                    </div>
                  )}
                </div>
              </div>
              <div className="text-xs text-slate-400 mt-3 line-clamp-2 min-h-[32px]">{t.description || "No description yet."}</div>
              <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                <div>v{t.current_version} · {t.sections?.length || 0} sections</div>
                <div className="mono">{t.document_prefix || "—"}-###</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {showNew && <NewTemplateDialog onClose={()=>setShowNew(false)} onCreated={()=>{ setShowNew(false); load(); }}/>}
    </div>
  );
}

function TemplateLogo({ template }) {
  const { activeWorkspace } = useAuth();
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let blob = null;
    if (!template.logo_path) { setSrc(null); return; }
    const token = localStorage.getItem("fx_token");
    fetch(`${BACKEND_URL}/api/workspaces/${activeWorkspace.id}/templates/${template.id}/logo`, { headers: { Authorization: `Bearer ${token}` }})
      .then((r) => r.ok ? r.blob() : null)
      .then((b) => { if (b) { blob = URL.createObjectURL(b); setSrc(blob); } });
    return () => { if (blob) URL.revokeObjectURL(blob); };
  }, [template.id, template.logo_path, activeWorkspace.id]);
  if (src) return <img src={src} alt="logo" className="w-10 h-10 rounded-md object-contain bg-white/5 p-1 shrink-0"/>;
  return <div className="w-10 h-10 rounded-md bg-gradient-to-br from-cyan-500/20 to-violet-500/20 border border-cyan-400/20 flex items-center justify-center shrink-0"><FileStack size={16} className="text-cyan-300"/></div>;
}

function NewTemplateDialog({ onClose, onCreated }) {
  const { activeWorkspace } = useAuth();
  const [tab, setTab] = useState("blank"); // blank | analyse
  const [form, setForm] = useState({ name:"", category:"Safe Work Instruction", description:"", document_prefix:"SWI" });
  const [busy, setBusy] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [extracted, setExtracted] = useState(null);

  const analyse = async (file) => {
    setAnalysing(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/workspaces/${activeWorkspace.id}/templates/analyse`, fd, { headers: {"Content-Type": "multipart/form-data"} });
      setExtracted({ ...data, source_file: file.name });
      if (data.hints?.document_prefix) setForm((f) => ({...f, document_prefix: data.hints.document_prefix}));
      if (data.hints?.title && !form.name) setForm((f) => ({...f, name: data.hints.title.substring(0, 60)}));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not analyse Word file");
    } finally { setAnalysing(false); }
  };

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form };
      if (extracted?.sections?.length) payload.sections = extracted.sections;
      await api.post(`/workspaces/${activeWorkspace.id}/templates`, payload);
      toast.success("Template added");
      onCreated();
    } catch { toast.error("Could not add template"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40" onClick={onClose}>
      <div className="fx-panel p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto fx-scrollbar" onClick={(e)=>e.stopPropagation()} data-testid="new-template-dialog">
        <div className="flex items-center justify-between mb-4"><div className="font-display text-lg font-semibold">Add template</div><button onClick={onClose} className="text-slate-500 hover:text-white"><X size={16}/></button></div>
        <div className="flex gap-1 mb-4 border-b border-[#1E293B]">
          <button onClick={()=>setTab("blank")} className={`px-3 py-2 text-xs font-medium ${tab==="blank" ? "text-white border-b-2 border-cyan-400" : "text-slate-400"}`} data-testid="tab-blank">Start blank</button>
          <button onClick={()=>setTab("analyse")} className={`px-3 py-2 text-xs font-medium ${tab==="analyse" ? "text-white border-b-2 border-cyan-400" : "text-slate-400"}`} data-testid="tab-analyse">Extract from Word</button>
        </div>

        {tab === "analyse" && (
          <div className="mb-4">
            <p className="text-xs text-slate-400 mb-2">Upload a reference .docx and Fexidoc will extract section headings, prefix and structure to seed this template.</p>
            <label className="block border-2 border-dashed border-[#1E293B] hover:border-cyan-400 rounded-lg p-4 text-center cursor-pointer">
              <UploadCloud size={18} className="mx-auto text-cyan-300"/>
              <div className="text-xs text-slate-300 mt-1">{analysing ? "Analysing…" : "Choose reference .docx"}</div>
              <input type="file" accept=".docx" className="hidden" onChange={(e) => e.target.files[0] && analyse(e.target.files[0])} data-testid="analyse-input"/>
            </label>
            {extracted && (
              <div className="mt-3 fx-panel-2 p-3">
                <div className="text-xs text-slate-400"><span className="text-cyan-300 mono">{extracted.source_file}</span> · {extracted.sections.length} sections · {extracted.paragraph_count} paragraphs</div>
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="extracted-sections">
                  {extracted.sections.map((s, i) => <span key={i} className="text-[11px] bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 px-2 py-0.5 rounded">{s.name}</span>)}
                </div>
              </div>
            )}
          </div>
        )}

        <form onSubmit={create} className="space-y-3">
          <Text label="Name" value={form.name} onChange={(v)=>setForm({...form, name:v})} testid="tpl-name"/>
          <label className="block">
            <div className="text-xs text-slate-400 mb-1">Category</div>
            <select value={form.category} onChange={(e)=>{ const v=e.target.value; setForm({...form, category:v, document_prefix: v.split(" ").map(w=>w[0]).join("").slice(0,3).toUpperCase()}); }} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100" data-testid="tpl-category">
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Text label="Document prefix" value={form.document_prefix} onChange={(v)=>setForm({...form, document_prefix:v})} testid="tpl-prefix"/>
            <Text label="Description" value={form.description} onChange={(v)=>setForm({...form, description:v})} testid="tpl-desc"/>
          </div>
          <button type="submit" disabled={busy || !form.name} className="fx-btn-primary w-full text-white font-medium py-2 rounded-lg disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="tpl-submit">{busy?"Adding…": (extracted ? <><Wand2 size={14}/> Create with {extracted.sections.length} extracted sections</> : "Add template")}</button>
        </form>
      </div>
    </div>
  );
}

function Text({label, value, onChange, testid}) {
  return (<label className="block"><div className="text-xs text-slate-400 mb-1">{label}</div><input required value={value} onChange={(e)=>onChange(e.target.value)} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 outline-none" data-testid={testid}/></label>);
}
